import { Bound } from "lib/@components/binding/Bound"
import { useState } from "react"
import { DifferenceWrapper } from "routes/schedule/compare/differenceWrapper"
import { Box, useTheme } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { BoundEditableRichText } from "lib/@components/editable/editableRichText"
import { DifferenceTitle } from "routes/schedule/compare/difference-title"
import { Status } from "routes/schedule/compare/status"
import { PropertyBox } from "lib/@components/property-box"
import { Comparison } from "routes/schedule/compare/lib/diff3"
import { EditModeSwitch } from "routes/schedule/compare/edit-mode-switch"
import { Editable } from "lib/@components/editable"
import { Approved } from "routes/schedule/compare/approved"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { getComparisonNormalizeHtml } from "routes/schedule/compare/get-comparison-normalizer"

export function RichTextDifference({ path, label = path, normalizeHtml }) {
    const { to, target } = useBoundContext()
    const [value1, setValue] = useBoundValue(path)
    const [original] = useState(value1)
    const [, setEditorBar] = useState()
    const value2 = Object.get(to, path, true)
    const [copied, setCopied] = useState(false)
    const [hover, setHover] = useState(false)
    const theme = useTheme()
    const normalizeHtmlValue =
        typeof normalizeHtml === "function"
            ? normalizeHtml
            : normalizeHtml
              ? getComparisonNormalizeHtml(target, to) ?? true
              : undefined

    return (
        <Bound editorBarNotification={setEditorBar}>
            <DifferenceWrapper label={`${target.title}: ${label}`}>
                <PropertyBox spacing={1} sx={{ wordBreak: "break-word" }}>
                    <DifferenceTitle label={label} />
                    <ListItemBox alignItems="flex-start">
                        <Box
                            flex={1}
                            ml={0}
                            mr={4}
                            sx={{ opacity: hover ? 0.25 : 1, transition: "opacity 0.2s linear" }}
                            data-cy="rich-text-difference-left"
                        >
                            <EditModeSwitch>
                                <Editable
                                    editor={<BoundEditableRichText size="small" fullWidth label="" field={path} />}
                                >
                                    <Box
                                        borderRadius={1}
                                        py={0.95}
                                        px={1.65}
                                        minHeight={40}
                                        sx={{ border: "1px solid", borderColor: "action.hover" }}
                                    >
                                        <Comparison
                                            original={value2}
                                            other={value2}
                                            text={value1}
                                            normalizeHtml={normalizeHtmlValue}
                                        />
                                    </Box>
                                </Editable>
                            </EditModeSwitch>
                        </Box>
                        <Approved>
                            <Status
                                path={path}
                                onHover={setHover}
                                revert={revert}
                                copy={copy}
                                onCopyChanged={setCopied}
                            />
                            <Box
                                flex={1}
                                px={3}
                                sx={{
                                    borderRadius: 1,
                                    opacity: copied ? 0.5 : 1,
                                    transition: "background-color linear 0.2s",
                                    bgcolor: hover ? `${theme.palette.primary.main}20` : undefined,
                                }}
                                data-cy="rich-text-difference-right"
                            >
                                <Box
                                    borderRadius={1}
                                    py={1}
                                    pl={2}
                                    pr={1}
                                    minHeight={40}
                                    sx={{ border: "1px solid", borderColor: "action.hover" }}
                                >
                                    <Comparison
                                        original={value1}
                                        other={value1}
                                        text={value2}
                                        showRemoved
                                        normalizeHtml={normalizeHtmlValue}
                                    />
                                </Box>
                            </Box>
                        </Approved>
                    </ListItemBox>
                </PropertyBox>
            </DifferenceWrapper>
        </Bound>
    )

    function copy() {
        setValue(value2)
    }

    function revert() {
        setValue(original)
    }
}
